import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LeftMessage from './LeftMessage';
import RightMessage from './RightMessage';
import TextMessage from './TextMessage';
import AttachmentMessage from './AttachmentMessage';
import EventMessage from './EventMessage';
import ReplyMessage from './ReplyMessage';
import { getCodeBlocks, getLookupWitHDefaultValue } from '../../../../utils/common';
import { linkify } from '../../../../utils/common';
import { botMessageType, GetChatLogic, messageType } from '../../ChatController';
import { useDispatch, useSelector } from 'react-redux';
import { sendDataThroughWebsocket } from '../../../../actions/websocket';
import { uploadFilesToChat } from '../../../../actions/conversations';
import { websocketEvents } from '../../../../utils/websocket';
import CardMessage from './CardMessage';
import UserPopup from '../../../../common/Popups/User';
import { FormattedGptMessage } from './FormattedGptMessage';

const messageAnimations = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.3, ease: 'easeInOut' }
};

const chipAnimations = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    hover: { scale: 1.05, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
    tap: { scale: 0.95 }
};

const typeComponentLookup = getLookupWitHDefaultValue({
    lookup: {
        [messageType.message]: TextMessage,
        [messageType.card]: CardMessage,
        [messageType.attachments]: AttachmentMessage,
        [messageType.reply]: ReplyMessage,
    },
    defaultValue: TextMessage,
});

const tagify = (text = '', tags = []) => {

    if (!text || tags.length === 0) return [text];

    const tagComponent = (tag) => (
        <UserPopup key={tag?.user?.userId} userId={tag?.user?.userId}>
            <motion.span
                whileHover={{ scale: 1.05 }}
                className='text-scogoorange cursor-pointer font-medium hover:underline'
            >
                {`@${tag?.user?.username}`}
            </motion.span>
        </UserPopup>
    );

    // Handle tags in format @[Username](userId)
    const regex = /@\[(.*?)\]\((.*?)\)/g;
    let matches = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
        matches.push({
            fullMatch: match[0],
            username: match[1],
            userId: match[2],
            index: match.index
        });
    }

    if (matches.length > 0) {
        const componentList = [];
        let lastIndex = 0;

        matches.forEach((match) => {
            // Add text before the tag
            if (match.index > lastIndex) {
                componentList.push(text.slice(lastIndex, match.index));
            }

            // Add the tag component
            componentList.push(
                <UserPopup key={match.userId} userId={match.userId}>
                    <motion.span
                        whileHover={{ scale: 1.05 }}
                        className='text-scogoorange cursor-pointer font-medium hover:underline'
                    >
                        {`@${match.username}`}
                    </motion.span>
                </UserPopup>
            );

            lastIndex = match.index + match.fullMatch.length;
        });

        // Add remaining text after the last tag
        if (lastIndex < text.length) {
            componentList.push(text.slice(lastIndex));
        }

        return componentList;
    }

    // Fall back to original tag handling if no @[Username](userId) format found
    const textBeforeFirstTag = text.slice(0, tags[0].position);
    const componentList = [];
    if (textBeforeFirstTag !== '') componentList.push(textBeforeFirstTag);
    tags.forEach((tag, idx) => {
        componentList.push(tagComponent(tag));
        const textAfterTagStartIndex = tag.position + tag?.user?.username?.length + 1;
        let textAfterTagEndIndex = text.length;
        const nextTag = tags[idx + 1];
        if (nextTag) {
            textAfterTagEndIndex = nextTag.position;
        }
        const textAfterTag = text.slice(textAfterTagStartIndex, textAfterTagEndIndex);
        componentList.push(textAfterTag);
    });
    return componentList;
};

export const formatTextWithCodeBlocks = ({ text = '', tags = [] }) => {
    let finalMsgList = [];
    let tagifiedMsgList = tagify(text, tags);
    tagifiedMsgList.forEach((segment) => {
        if (typeof segment === 'string' && segment.trim().startsWith('##')) {
            segment = segment.split(' ');
            let message = <motion.span whileHover={{ color: '#4A5568' }} className='text-scogobgsky font-semibold'>{segment.slice(0, 1)}</motion.span>;
            finalMsgList.push(message);
            finalMsgList.push(` ${segment.slice(1).join(' ')}`);
        } else if (typeof segment === 'string') {
            const codeBlocks = getCodeBlocks(segment);
            codeBlocks.forEach((block) => {
                if (typeof block === 'string') {
                    finalMsgList = [...finalMsgList, ...linkify(block)];
                } else {
                    finalMsgList.push(block);
                }
            });
        } else {
            finalMsgList.push(segment);
        }
    });
    return finalMsgList;
};

export const formatText = ({ text = '', tags = [] }) => {
    let finalMsgList = [];
    const tagifiedMsgList = tagify(text, tags);
    tagifiedMsgList.forEach((segment) => {
        if (typeof segment != 'string') finalMsgList.push(segment);
        else finalMsgList = [...finalMsgList, ...linkify(segment)];
    });
    return finalMsgList;
};

const greetOptions = [
    { text: 'Payment', category: 'root' },
    { text: 'Wallet', category: 'root' },
    { text: 'Approvals', category: 'root' },
    { text: 'Ticket', category: 'root' },
    { text: 'App/Web issues', category: 'root' },
    { text: 'How to Scogo', category: 'root' },
    { text: 'Others', trigger: 'bot-disable-assign' },
];

const likeDislikeOption = [{ text: '👍' }, { text: '👎' }];

function Message({ side = 'right', type = 'message', message, onReplyClick, selectedMessageId, replyShow, onClickReplyMessage, createdBy, showMessageAction, toEnableChip, showReplyIcon, messageOnclick = () => { }, isSearchMessage, showJumbIcon = false, callStepFields, ticketId, isChatPopup, isTicketChat }) {
    const dispatch = useDispatch();
    const [isEditMessage, setIsEditMessage] = useState(false);
    const [showActions, setShowActions] = useState(showMessageAction);
    const messageActions = GetChatLogic('messageAction');
    const botsMessage = message?.payload?.fulfillmentMessages ? message?.payload?.fulfillmentMessages[0] : false;

    const chips = (conversationId) => {
        if (!toEnableChip) return null;

        let options = [];
        if (botsMessage?.message === 'payload') {
            const { input } = botsMessage.payload;
            if (input.type === botMessageType.Greet) {
                options = greetOptions;
            } else if (input.type === botMessageType.FAQ || input.type === botMessageType.Video) {
                options = likeDislikeOption;
            } else {
                options = input?.options;
            }
        }

        return (
            <motion.div
                className="w-full md:w-2/3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
            >
                <motion.ul className='flex flex-wrap list-none mt-3 gap-2'>
                    <AnimatePresence>
                        {options.map((option, index) => (
                            <motion.li
                                key={index}
                                className='text-center text-font10 text-gray-700 border border-gray-300 rounded-full px-4 py-2 cursor-pointer transition-all hover:text-white hover:bg-scogoorange hover:border-scogoorange shadow-sm'
                                {...chipAnimations}
                                transition={{ delay: index * 0.1 }}
                                onClick={() => { }}
                            >
                                {option.text}
                            </motion.li>
                        ))}
                    </AnimatePresence>
                </motion.ul>
            </motion.div>
        );
    };

    useEffect(() => {
        let show = true;
        let isCurrentMessageSelected = replyShow && selectedMessageId === message._id;
        if (isEditMessage || isCurrentMessageSelected || !message.timestamp || !showMessageAction) show = false;
        setShowActions(show);
    }, [message, isEditMessage, replyShow, selectedMessageId, showMessageAction]);

    const onRetryMessage = useCallback((message) => {
        const conversationId = message.conversation;
        if ([messageType.message, messageType.reply].includes(message.type)) {
            dispatch(
                sendDataThroughWebsocket({
                    action: websocketEvents.chat.sendMessage,
                    data: {
                        conversation: conversationId,
                        type: message.type,
                        message: message.message,
                        repliedTo: message.repliedTo,
                        messageId: message.messageId,
                    },
                    from: message.from,
                })
            );
        }
        if (message.type === messageType.attachments) {
            dispatch(
                uploadFilesToChat({
                    _id: conversationId,
                    fileData: message.fileData,
                    from: message.from,
                    messageId: message.messageId,
                })
            );
        }
    }, [dispatch]);

    useEffect(() => {
        if (!message.isEditLoading) setIsEditMessage(false);
    }, [message]);

    const sideComponetLookup = {
        left: LeftMessage,
        right: RightMessage,
    };

    const isScogoGptChat = message?.conversation?.type === 'gpt';
    const SideComponent = sideComponetLookup[side];
    let TypeComponent = isScogoGptChat ? FormattedGptMessage :
        message?.media?.length > 0 ? AttachmentMessage :
            message?.replied_to ? ReplyMessage :
                TextMessage;

    const getMessageClass = () => {
        let className = 'py-3 messagesideComponent';
        if (selectedMessageId === message?.id) className += ' bg-blue-100 rounded-lg';
        return className;
    };

    let messageBody;
    if (type === 'EVENT') {
        messageBody = <EventMessage message={message} isChatPopup={isChatPopup} />;
    } else {
        messageBody = (
            <motion.div
                className={getMessageClass()}
                {...messageAnimations}
                layout
            >
                <SideComponent
                    message={message}
                    onReplyClick={onReplyClick}
                    chips={chips}
                    messageActions={messageActions}
                    onRetryMessage={onRetryMessage}
                    isEditMessage={isEditMessage}
                    setIsEditMessage={setIsEditMessage}
                    showActions={showActions}
                    showReplyIcon={showReplyIcon && !isScogoGptChat}
                    fullWidth={message.type === messageType.card}
                    messageOnclick={messageOnclick}
                    isSearchMessage={isSearchMessage}
                    showJumbIcon={showJumbIcon}
                    ticketId={ticketId}
                >
                    {(propsBasedOnSide) => (
                        <TypeComponent
                            {...propsBasedOnSide}
                            onClickReplyMessage={onClickReplyMessage}
                            createdBy={createdBy}
                            message={message}
                            formatText={formatText}
                            formatTextWithCodeBlocks={formatTextWithCodeBlocks}
                            isSearchMessage={isSearchMessage}
                            callStepFields={callStepFields}
                            isTicketChat={isTicketChat}
                            isChatPopup={isChatPopup}
                        />
                    )}
                </SideComponent>
            </motion.div>
        );
    }

    return (
        <motion.div
            id={message.id}
            className='message_class_for_scroll mb-1'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            layout
        >
            {messageBody}
        </motion.div>
    );
}

export default Message;
